import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * About / How-It-Works page.
 *
 * Purpose: keep the Landing page focused on the START ORDER CTA while
 * preserving the SEO-rich, human-friendly explanation of what this site
 * is, who runs it, and how ordering works. The Landing page links here
 * ("Learn more") and Google crawls it through the sitemap.
 */
export default function About() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b-2 border-slate-900 bg-white/95 px-5 py-3 backdrop-blur sm:px-10">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <button
            data-testid="about-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-600 hover:text-[#1d4ed8]"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.5} />
            Back
          </button>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-600">
            Gaither High · Tampa, FL
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 sm:px-10">
        <div className="space-y-6" data-testid="about-content">
          <div>
            <h1 className="font-heading text-3xl tracking-wider text-slate-900 sm:text-4xl">
              Order Concessions From Your Seat
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">
              Gaither Cowboys Concessions is the official online ordering
              site for the Gaither High School Athletic Boosters in Tampa,
              Florida. Skip the halftime line, order food and drinks from
              the stands, pay with your phone, and pick everything up hot
              at the Express Window. Every dollar you spend goes back to
              the student-athletes who wear the Cowboys jersey.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg tracking-wider text-slate-900">
              How It Works
            </h2>
            <ol className="mt-2 space-y-2 text-sm leading-relaxed text-slate-700 sm:text-base">
              <li>
                <strong>1. Scan the QR</strong> on the poster near your
                seat, or type our address into a browser. No app to
                download.
              </li>
              <li>
                <strong>2. Build your order</strong> — burgers, hot dogs,
                chicken sandwiches, snacks, sodas, waters, and combo
                deals. Chicken sandwich comes grilled or breaded.
              </li>
              <li>
                <strong>3. Pay securely</strong> with Apple Pay, Google
                Pay, or any major credit card via Stripe. There is a
                small online order fee that keeps this system running
                for the boosters.
              </li>
              <li>
                <strong>4. Track your order</strong> live on your phone
                as it moves from Pending to Preparing to Ready. You will
                get a friendly nudge when it is time to walk down.
              </li>
              <li>
                <strong>5. Grab it at the Express Window</strong> —
                skipping the regular line entirely. Say hi to the
                volunteer boosters behind the counter.
              </li>
            </ol>
          </div>

          <div>
            <h2 className="font-heading text-lg tracking-wider text-slate-900">
              Ordering Ahead for Halftime or Quarters
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">
              Beat the rush by pre-ordering during the first quarter for
              pickup at halftime, the third quarter, the fourth quarter,
              or the end of the game. Type in your preferred pickup
              window at checkout and our kitchen will time your food so
              it comes off the grill fresh.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-lg tracking-wider text-slate-900">
              Supporting Gaither Cowboys Sports
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">
              The Gaither Athletic Boosters is an all-volunteer group of
              parents, alumni, and local supporters who fund equipment,
              travel, and scholarships for the football team and every
              other sport that wears the Cowboys jersey. Every plate
              sold on this site helps a Gaither athlete take the field
              better prepared. If you want to sponsor the boosters, get
              in touch — sponsor logos appear on the home page and on
              printed programs at every home game.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="border-l-2 border-[#1d4ed8] pl-3">
              <div className="font-heading text-xs tracking-[0.2em] text-[#1d4ed8]">
                HOURS
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Home game nights, Friday kickoff through the fourth
                quarter. Also open at select basketball games.
              </div>
            </div>
            <div className="border-l-2 border-[#1d4ed8] pl-3">
              <div className="font-heading text-xs tracking-[0.2em] text-[#1d4ed8]">
                LOCATION
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Gaither High School, Tampa, FL — Express Window at the
                stadium concession stand.
              </div>
            </div>
            <div className="border-l-2 border-[#1d4ed8] pl-3">
              <div className="font-heading text-xs tracking-[0.2em] text-[#1d4ed8]">
                PAYMENT
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Card, Apple Pay, Google Pay. All processed by Stripe on
                a secure connection.
              </div>
            </div>
          </div>

          <div className="pt-4">
            <button
              data-testid="about-start-order-btn"
              onClick={() => navigate("/menu")}
              className="inline-flex items-center gap-2 border-2 border-[#1d4ed8] bg-[#1d4ed8] px-6 py-3 font-heading text-lg tracking-wider text-white hover:bg-[#1e40af]"
            >
              START ORDER →
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
